import React, {useEffect, useState} from 'react';
import {
    Grid,
    ListItem,
    ListItemIcon,
    Checkbox,
    Button,
    Typography,
} from '@material-ui/core';
import {MyAccordion, MyAccordionSummary, MyAccordionDetails} from '../Accordions';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {NavLink} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {H6} from 'components/TypographyShortcuts';
import InfoModal from 'components/widgets/tooltips/InfoModal';
import {usePutApplicationChecklist} from 'hooks';
import sectionContent from 'components/widgets/checklists/ChecklistSectionContent';
import MMCinternational from 'components/mmld/MMCtools/MMCinternational';

const useStyles = makeStyles(theme => ({
    root: {
        [theme.breakpoints.down('sm')]: {
            width: '20em',
            height: '15em',
        },
        [theme.breakpoints.up('md')]: {
            width: '40em',
            height: '30em',
        },
    },
    accordion: {
        justifyContent: 'stretch',
        background: theme.palette.gray.light,
        '&.complete': {
            background:
                theme.palette.type === 'dark'
                    ? theme.palette.success.dark
                    : theme.palette.success.light,
        },
    },
    summary: {
        background: theme.palette.gray.light,
        '&.complete': {
            background:
                theme.palette.type === 'dark'
                    ? theme.palette.success.dark
                    : theme.palette.success.light,
        },
    },
    checkbox: {
        color: theme.palette.primary.main,
    },
}));

const ChecklistSectionTrainingSTCW = ({
    sectionId,
    checkoffs,
    complete,
    setComplete,
    mmld,
}) => {
    const classes = useStyles();

    const {put: putApplication, result} = usePutApplicationChecklist();

    const {labelId, title, link, link_text, fields, infoModalContent} =
        sectionContent?.[sectionId];

    const pruneData = sourceOfTruth =>
        fields.reduce((object, item) => {
            return {
                ...object,
                [item]: sourceOfTruth?.application_checklist[item] || false,
            };
        }, {});
    const sourceOfTruth = result || checkoffs;
    const [prunedData, setPrunedData] = useState(pruneData(sourceOfTruth));

    const handleChange = value => {
        const newData = {
            ...prunedData,
            [value]: !prunedData[value],
        };
        setPrunedData(newData);
        putApplication(newData);
    };

    useEffect(() => {
        if (result) {
            const thisSectionComplete = result?.application_checklist?.[labelId];
            setComplete(thisSectionComplete);
        } else {
            const thisSectionComplete = checkoffs?.application_checklist?.[labelId];
            setComplete(thisSectionComplete);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoffs, result]);

    // Handle the POST updates in case a checkbox isn't allowed to be checked
    useEffect(() => {
        if (result || checkoffs) {
            setPrunedData(pruneData(sourceOfTruth));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoffs, result, sourceOfTruth]);

    return (
        <MyAccordion
            className={`${classes.accordion} ${complete ? 'complete' : ''}`}
        >
            <MyAccordionSummary
                className={`${classes.summary} ${complete ? 'complete' : ''}`}
                expandIcon={<ExpandMoreIcon />}
            >
                <Grid container className={classes.titleContainer}>
                    <Grid
                        item
                        style={{
                            display: 'flex',
                            justifyContent: 'left',
                        }}
                        xs={12}
                    >
                        {title ? (
                            <H6>
                                <strong>{title}</strong>
                            </H6>
                        ) : null}
                        <InfoModal content={infoModalContent} />
                    </Grid>
                </Grid>
            </MyAccordionSummary>
            <MyAccordionDetails
                className={`${classes.summary} ${complete ? 'complete' : ''}`}
            >
                <Grid container>
                    <Grid item style={{padding: '1rem'}}>
                        <MMCinternational
                            mmld={mmld}
                            doc_type='MMC'
                            noheader='true'
                        />
                    </Grid>

                    <ListItem key={labelId} role='button' dense button>
                        <ListItemIcon
                            onClick={() => {
                                handleChange(labelId);
                            }}
                            style={{alignSelf: 'flex-start'}}
                        >
                            <Checkbox
                                className={classes.checkbox}
                                edge='start'
                                checked={prunedData[labelId]}
                                disableRipple
                                inputProps={{
                                    'aria-labelledby': labelId,
                                }}
                            />
                        </ListItemIcon>
                        <Grid container direction='column'>
                            <Grid>
                                {checkoffs.application_checklist.ss_days_5_years >=
                                360 ? (
                                    <Grid item>
                                        <Typography variant='body2'>
                                            <strong>
                                                You have over 360 days in the last
                                                five years.
                                            </strong>
                                        </Typography>
                                        <p></p>
                                        <Typography variant='body2'>
                                            <strong>
                                                To renew your STCW Officer and/or
                                                Ratings you should take an STCW Basic
                                                Training Revalidation course.
                                            </strong>
                                        </Typography>
                                        <Typography variant='body2'>
                                            You can also take an Original or
                                            Refresher Basic Training course if you
                                            can not find a Revalidation course that
                                            matches your schedule. This course needs
                                            to be within the last 5-years.
                                        </Typography>
                                        <p></p>
                                        <Typography variant='body2'>
                                            <strong>
                                                To renew your Advanced Firefighting
                                                if you hold it, you should take an
                                                Advanced Firefighting Revalidation
                                                course.
                                            </strong>
                                        </Typography>
                                        <Typography variant='body2'>
                                            You can also take an Original or
                                            Refresher Advanced Firefighting course if
                                            you can not find the Revalidation course
                                            that matches your schedule. This course
                                            needs to be within the last 5-years.
                                        </Typography>
                                        <p></p>
                                        <Typography variant='body2'>
                                            Check the box when you have uploaded all
                                            of your STCW Training Certificates to
                                            MM-SEAS{' '}
                                        </Typography>
                                        <p></p>
                                    </Grid>
                                ) : (
                                    <Grid item>
                                        <Typography variant='body2'>
                                            <strong>
                                                You have less than 360 days in the
                                                last five years.
                                            </strong>
                                        </Typography>
                                        <p></p>
                                        <Typography variant='body2'>
                                            To renew your STCW Officer and/or Ratings
                                            you need to take a STCW Basic Training
                                            Refresher course or ALL of the Original
                                            Basic Training courses again within the
                                            last 5-years.
                                        </Typography>
                                        <p></p>
                                        <Typography variant='body2'>
                                            To renew your Proficiency in survival
                                            craft and rescue boats other than fast
                                            rescue boats (PSC) if you hold it, you
                                            need to take a PSC Refresher course or
                                            Original course within the last 5-years.
                                        </Typography>
                                        <p></p>
                                        <Typography variant='body2'>
                                            To renew your Advanced Firefighting if
                                            you hold it, you need to take an Original
                                            or Refresher Advanced Firefighting course
                                            within the last 5-years.
                                        </Typography>
                                        <p></p>
                                        <Typography variant='body2'>
                                            Check the box when you have uploaded all
                                            of your STCW Training Certificates to
                                            MM-SEAS
                                        </Typography>
                                        <p></p>
                                    </Grid>
                                )}
                            </Grid>

                            {link ? (
                                <Grid item>
                                    <Button
                                        component={NavLink}
                                        to={link}
                                        variant='contained'
                                        color='secondary'
                                        style={{width: 'fit-content'}}
                                    >
                                        {link_text}
                                    </Button>
                                </Grid>
                            ) : null}
                        </Grid>
                    </ListItem>
                </Grid>
            </MyAccordionDetails>
        </MyAccordion>
    );
};
export default ChecklistSectionTrainingSTCW;
